import { useMediaQuery } from "react-responsive";

// Fonts
export const rootFontSize = 16;

/**
 * Converts pixels to a rem value.
 * @example pxToRem(32) // '2rem'
 * pxToRem('32') // '2rem'
 * pxToRem('32px') // '2rem'
 */
export const pxToRem = (px: number | string): string =>
  `${(Number(px.toString().replace("px", "")) / rootFontSize).toFixed(2)}rem`;

export const publicBasePath = "/playoffs";

const fallbacks = `system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji'`;
export const FONTS = {
  PRIMARY: `'Stainless-Regular', ${fallbacks}`,
  SECONDARY: `BigBill-Oblique, ${fallbacks}`,
};

export const breakpoints = {
  mobileMid: "460px",
  mobile: "800px",
  tablet: "936px",
  laptop: "1200px",
  xxlarge: "1440px",
  contentMaxWidth: `1280px`,
};

const customMediaQuery = (maxWidth = "0px") => `@media (max-width: ${maxWidth})`;
/**
 * Quick max-width media queries.
 */
export const media = {
  custom: customMediaQuery,
  mobileMid: customMediaQuery(breakpoints.mobileMid),
  mobile: customMediaQuery(breakpoints.mobile),
  tablet: customMediaQuery(breakpoints.tablet),
  xxlarge: customMediaQuery(breakpoints.xxlarge),
};

export const useBreakpoints = () => {
  const isMobileMid = useMediaQuery({
    query: `(max-width: ${breakpoints.mobileMid})`,
  });
  const isMobile = useMediaQuery({
    query: `(max-width: ${breakpoints.mobile})`,
  });

  const isTabletFloor = useMediaQuery({
    query: `(max-width: ${breakpoints.mobile + 1})`,
  });
  const isTabletCeil = useMediaQuery({
    query: `(max-width: ${breakpoints.tablet})`,
  });

  const isLaptopFloor = useMediaQuery({
    query: `(max-width: ${breakpoints.tablet + 1})`,
  });
  const isLaptopCeil = useMediaQuery({ query: "(max-width: 1440px)" });

  const isXHDFloor = useMediaQuery({ query: "(max-width: 1441px)" });
  const isXHDCeil = useMediaQuery({ query: "(max-width: 4096px)" });

  return {
    isMobileMid,
    isMobile,
    isTabletFloor,
    isTabletCeil,
    isLaptopFloor,
    isLaptopCeil,
    isXHDFloor,
    isXHDCeil,
  };
};

/**
 * Applies alpha to a hex value.
 * @arg alpha default value: 1
 * @example
 * hexRGBA('#335596', 0.25); // rgba(51, 85, 150, 0.25)
 */
export function hexRGBA(hex: string, alpha = 1): string {
  if (!/^#([A-Fa-f0-9]{3}){1,2}$/.test(hex)) {
    console.error(`Bad Hex Value: hexRGBA(${hex})`);
    return hex;
  }

  let c;
  c = hex.substring(1).split("");
  if (c.length == 3) {
    c = [c[0], c[0], c[1], c[1], c[2], c[2]];
  }
  c = `0x${c.join("")}`;

  return `rgba(${[(c >> 16) & 255, (c >> 8) & 255, c & 255].join(", ")}, ${alpha})`;
}
